/**
 * ADU-specific prompt templates for the chat prompt library (K4).
 *
 * Categories reflect the core ADU Lab product flow:
 * assessment → zoning → design → budget → report analysis.
 */

export interface PromptTemplate {
  id: string
  text: string
  label: string
}

export interface PromptCategory {
  id: string
  label: string
  icon: string // Material Icons name
  prompts: PromptTemplate[]
}

export const PROMPT_CATEGORIES: PromptCategory[] = [
  {
    id: "getting_started",
    label: "Getting Started",
    icon: "lightbulb",
    prompts: [
      { id: "gs1", label: "What is an ADU?", text: "What's an ADU and can I build one on my property?" },
      { id: "gs2", label: "ADU planning process", text: "Walk me through the ADU planning process step by step." },
      { id: "gs3", label: "Common challenges", text: "What are the biggest challenges homeowners face with ADU projects?" },
    ],
  },
  {
    id: "zoning",
    label: "Zoning & Regulations",
    icon: "gavel",
    prompts: [
      { id: "z1", label: "Setback requirements", text: "What are the zoning setback requirements for my property?" },
      { id: "z2", label: "Maximum ADU size", text: "What's the maximum size ADU I can build on my lot?" },
      { id: "z3", label: "Owner-occupancy rules", text: "Do I need to live on the property to build an ADU?" },
      { id: "z4", label: "Permitted ADU types", text: "What types of ADUs are permitted in my zoning district?" },
    ],
  },
  {
    id: "design",
    label: "Design & Construction",
    icon: "architecture",
    prompts: [
      { id: "d1", label: "Best ADU type for my lot", text: "What ADU type would work best for my property — detached, attached, or garage conversion?" },
      { id: "d2", label: "Garage conversion vs new build", text: "Compare a garage conversion versus new construction for my property." },
      { id: "d3", label: "Cost-effective floor plans", text: "What are the most cost-effective ADU floor plans for a 400–600 sq ft unit?" },
    ],
  },
  {
    id: "budget",
    label: "Budget & Financing",
    icon: "payments",
    prompts: [
      { id: "b1", label: "Total project budget", text: "What should I budget for a complete ADU project including permits, construction, and landscaping?" },
      { id: "b2", label: "Financing options", text: "What financing options are available for ADU construction?" },
      { id: "b3", label: "Cost breakdown", text: "Break down the typical costs for an ADU: permits, design, construction, and utilities." },
      { id: "b4", label: "ROI analysis", text: "What's the typical return on investment for an ADU as a rental unit?" },
    ],
  },
  {
    id: "analysis",
    label: "My Project Analysis",
    icon: "analytics",
    prompts: [
      { id: "a1", label: "Property data insights", text: "Analyze my property data and highlight the key insights for my ADU project." },
      { id: "a2", label: "SPAR score explained", text: "What does my SPAR score mean, and what should I focus on to improve it?" },
      { id: "a3", label: "Report summary", text: "Summarize the key findings from my 9-category ADU project report." },
      { id: "a4", label: "Next steps", text: "Based on my project data, what are the recommended next steps?" },
    ],
  },
]
